'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import NamesSearchOverlay from '@/components/NamesSearchOverlay';
import GlobalSearchOverlay from '@/components/GlobalSearchOverlay';
import { createClient } from '@/lib/supabase/client';

const journeyLinks = [
  { label: 'Open Letter: Deep Green', href: '/open-letter', pinned: true },
  { label: '— Journey —', href: '#' },
  { label: 'Guided Intro', href: '/guided-intro' },
  { label: 'Guided Walkthrough', href: '/guided-walkthrough' },
  { label: 'Community', href: '/community' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation' },
  { label: 'Inner Reality Lens', href: '/inner-reality-lens' },
  { label: 'Creation Lens', href: '/creation-lens' },
  { label: 'Ecosystem Journey', href: '/ecosystem-journey' },
  { label: 'IRL Lens Walkthrough', href: '/irl-lens' },
  { label: 'Muslim Resource Guide', href: '/muslim-resource-guide' },
  { label: 'Names Reference', href: '/names-reference' },
  { label: '— Subhaals —', href: '#' },
  { label: 'Masjid Al-Ihsan', href: '/subhaal/masjid-al-ihsan' },
  { label: 'Subhaal Hub', href: '/subhaal' },
  { label: '— My Journey —', href: '#' },
  { label: 'My Journey Dashboard', href: '/user-dashboard' },
  { label: 'Saved Collection', href: '/user-dashboard?tab=saved' },
  { label: 'Admin Moderation', href: '/admin-moderation' },
];

// ─── MOBILE DRAWER SECTIONS ───────────────────────────────────────────────────
const MOBILE_SECTIONS = [
  {
    id: 'my-journey',
    label: 'My Journey',
    icon: '◎',
    links: [
      { label: 'Open Letter: Deep Green', href: '/open-letter', pinned: true },
      { label: 'Guided Intro', href: '/guided-intro' },
      { label: 'Guided Walkthrough', href: '/guided-walkthrough' },
      { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal' },
      { label: 'Ayat in Creation', href: '/ayat-in-creation' },
      { label: 'Inner Reality Lens', href: '/inner-reality-lens' },
      { label: 'Creation Lens', href: '/creation-lens' },
      { label: 'Ecosystem Journey', href: '/ecosystem-journey' },
      { label: 'IRL Lens Walkthrough', href: '/irl-lens' },
      { label: 'Names Reference', href: '/names-reference' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: '⬡',
    links: [
      { label: 'Community Hub', href: '/community' },
      { label: 'Fisabilillah Feed', href: '/community#fisabilillah-feed' },
      { label: 'Observation Chain Archive', href: '/observation-chain-archive' },
      { label: 'Impact Analytics', href: '/impact' },
      { label: 'Followers & Cohorts', href: '/followers' },
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide' },
      { label: 'Quests', href: '/quests' },
    ],
  },
  {
    id: 'subhaals',
    label: 'Subhaals',
    icon: '🌿',
    links: [
      { label: 'Subhaal Hub', href: '/subhaal' },
      { label: 'Masjid Al-Ihsan', href: '/subhaal/masjid-al-ihsan' },
      { label: 'Chicago Muslim Resources', href: '/subhaal/masjid-al-ihsan/resource-guide' },
    ],
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: '✦',
    links: [
      { label: 'Monolyth (VR)', href: '/portfolio/monolyth' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: '⌘',
    links: [
      { label: 'My Journey Dashboard', href: '/user-dashboard' },
      { label: 'Saved Collection', href: '/user-dashboard?tab=saved' },
      { label: 'Roadmap', href: '/user-dashboard?tab=roadmap' },
      { label: 'Admin Moderation', href: '/admin-moderation' },
      { label: 'Admin Dashboard', href: '/admin-dashboard' },
      { label: 'Quest Templates', href: '/admin-dashboard' },
      { label: 'MANA Newsletter', href: '/mana-newsletter' },
      { label: 'MANA Crawler', href: '/mana-crawler' },
      { label: 'Guardian Review', href: '/guardian-review' },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: '◈',
    links: [
      { label: 'SL Glossary', href: '/sl-glossary' },
      { label: 'Bridge: Environment Design', href: '/bridge-environment-design' },
      { label: 'Bridge: IRL Application', href: '/bridge-irl-application' },
      { label: 'Bridge: Resource Guide', href: '/bridge-resource-guide' },
    ],
  },
];

interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string;
  link?: string;
  is_read: boolean;
  actor_name?: string;
  actor_role?: string;
  created_at: string;
}

const NOTIF_ICONS: Record<string, string> = {
  quest_reaction: '⌘',
  garment_featured: '⬡',
  comment_reply: '◎',
  milestone: '✦',
  badge_earned: '◈',
};

const NOTIF_COLORS: Record<string, string> = {
  quest_reaction: '#D4A82A',
  garment_featured: '#2d8a87',
  comment_reply: '#8a8ab8',
  milestone: '#2d7a47',
  badge_earned: '#c9a84c',
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Header({ onSearchOpen }: { onSearchOpen?: () => void } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  // Accordion state: which mobile section is open
  const [openSection, setOpenSection] = useState<string | null>('my-journey');
  const notifRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const supabase = createClient();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close notif panel on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    if (notifOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notifOpen]);

  const loadNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        const { data } = await supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(12);
        setNotifications(data || []);
        setUnreadCount((data || []).filter((n: Notification) => !n.is_read).length);
      } else {
        const { data } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(12);
        setNotifications(data || []);
        setUnreadCount((data || []).filter((n: Notification) => !n.is_read).length);
      }
    } catch {
      // silently fail
    }
    setNotifLoading(false);
  }, [supabase]);

  // Initial load + real-time subscription
  useEffect(() => {
    loadNotifications();

    const channel = supabase
      .channel('notifications-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, () => {
        loadNotifications();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [loadNotifications, supabase]);

  // ⌘K / Ctrl+K shortcut to open global search
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
      // '/' shortcut — open search (when not typing in an input)
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
      // '?' shortcut — open help modal
      if (e.key === '?' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault();
        setHelpOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  async function markAllRead() {
    const ids = notifications.filter(n => !n.is_read).map(n => n.id);
    if (ids.length === 0) return;
    await supabase.from('notifications').update({ is_read: true }).in('id', ids);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }

  async function markOneRead(id: string) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }

  const isHomepage = pathname === '/homepage' || pathname === '/';

  function handleSearchOpen() {
    if (onSearchOpen) {
      onSearchOpen();
    } else {
      setGlobalSearchOpen(true);
    }
  }

  return (
    <>
      {!onSearchOpen && <NamesSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />}
      {!onSearchOpen && <GlobalSearchOverlay open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />}

      {/* ── KEYBOARD HELP MODAL ─────────────────────────────────────── */}
      {helpOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
          style={{
            position: 'fixed', inset: 0, zIndex: 500,
            background: 'rgba(6,13,18,0.92)', backdropFilter: 'blur(16px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setHelpOpen(false)}
        >
          <div
            role="document"
            style={{
              background: '#0d1a14', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 8, padding: '32px 36px', maxWidth: 480, width: '90vw',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.22em', textTransform: 'uppercase', marginBottom: 4 }}>
                  ◈ Keyboard Shortcuts
                </p>
                <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: '#F5F0E8', fontWeight: 400 }}>
                  Navigate with intention
                </h2>
              </div>
              <button
                onClick={() => setHelpOpen(false)}
                aria-label="Close keyboard shortcuts"
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', padding: '4px 10px', borderRadius: 3, fontFamily: 'monospace', fontSize: 10 }}
              >
                ESC
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { keys: ['/', '⌘K'], desc: 'Open global search' },
                { keys: ['?'], desc: 'Show this help panel' },
                { keys: ['ESC'], desc: 'Close any overlay or modal' },
                { keys: ['↑', '↓'], desc: 'Navigate search results' },
                { keys: ['↵'], desc: 'Open selected result' },
                { keys: ['←', '→'], desc: 'Navigate Ayat Constellation nodes' },
              ].map(({ keys, desc }) => (
                <div key={desc} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,240,232,0.6)' }}>{desc}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {keys.map(k => (
                      <kbd key={k} style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: '3px 8px', background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)', borderRadius: 3,
                        fontFamily: 'monospace', fontSize: 11, color: '#c9a84c',
                        minWidth: 28,
                      }}>{k}</kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.2)', letterSpacing: '.12em', textTransform: 'uppercase', marginTop: 20, textAlign: 'center' }}>
              Press ? anytime to return here
            </p>
          </div>
        </div>
      )}

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'nav-glass-dark shadow-sm border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-18">

            {/* Logo */}
            <Link href="/homepage" className="flex items-center gap-3 group">
              <AppLogo size={40} text="HaalProject" onClick={() => {}} />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                href="/homepage"
                className={`font-mono text-xs tracking-widest uppercase transition-colors duration-200 ${
                  isHomepage ? 'text-white' : 'text-white/50 hover:text-white'
                }`}
              >
                Hub
              </Link>

              <Link
                href="/open-letter"
                className="font-mono text-xs tracking-widest uppercase transition-colors duration-200 flex items-center gap-1"
                style={{
                  color: pathname === '/open-letter' ? '#D4A82A' : 'rgba(179,138,42,0.75)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#D4A82A'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = pathname === '/open-letter' ? '#D4A82A' : 'rgba(179,138,42,0.75)'; }}
              >
                <span style={{ fontSize: 9 }}>✦</span>
                Open Letter
              </Link>

              <Link
                href="/sl-glossary"
                className="font-mono text-xs tracking-widest uppercase transition-colors duration-200 flex items-center gap-1"
                style={{
                  color: pathname === '/sl-glossary' ? '#8a8ab8' : 'rgba(138,138,184,0.65)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#8a8ab8'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = pathname === '/sl-glossary' ? '#8a8ab8' : 'rgba(138,138,184,0.65)'; }}
              >
                <span style={{ fontSize: 9 }}>◎</span>
                SL Glossary
              </Link>

              <Link
                href="/portfolio/monolyth"
                className="font-mono text-xs tracking-widest uppercase transition-colors duration-200 flex items-center gap-1"
                style={{
                  color: pathname === '/portfolio/monolyth' ? '#c9a84c' : 'rgba(201,168,76,0.5)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = pathname === '/portfolio/monolyth' ? '#c9a84c' : 'rgba(201,168,76,0.5)'; }}
              >
                <span style={{ fontSize: 9 }}>◼</span>
                Portfolio
              </Link>

              <Link
                href="/user-dashboard"
                className="font-mono text-xs tracking-widest uppercase transition-colors duration-200 flex items-center gap-1"
                style={{
                  color: pathname === '/user-dashboard' ? '#c9a84c' : 'rgba(201,168,76,0.45)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = pathname === '/user-dashboard' ? '#c9a84c' : 'rgba(201,168,76,0.45)'; }}
              >
                <span style={{ fontSize: 9 }}>◈</span>
                My Journey
              </Link>

              {/* Journey Dropdown */}
              <div className="relative" onMouseEnter={() => setJourneyOpen(true)} onMouseLeave={() => setJourneyOpen(false)}>
                <button className="font-mono text-xs tracking-widest uppercase text-white/50 hover:text-white transition-colors duration-200 flex items-center gap-1.5">
                  Journey
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {journeyOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 border border-white/10 shadow-2xl" style={{ background: '#1C2B25' }}>
                    {journeyLinks?.map((link, i) => (
                      link.href === '#' ? (
                        <div
                          key={link?.href + i}
                          className="px-4 py-2 font-mono text-xs tracking-wider border-b border-white/5 text-white/20 border-t border-white/10 mt-1"
                        >
                          {link?.label}
                        </div>
                      ) : link.pinned ? (
                        <Link
                          key={link?.href}
                          href={link?.href}
                          className={`block px-4 py-3 font-mono text-xs tracking-wider transition-colors duration-150 border-b-2 ${
                            pathname === link?.href ? 'text-white' : 'text-yellow-400/90 hover:text-yellow-300'
                          }`}
                          style={{ borderColor: 'rgba(179,138,42,0.5)', background: 'rgba(139,105,20,0.12)' }}
                        >
                          <span className="mr-2">✦</span>
                          {link?.label}
                        </Link>
                      ) : (
                      <Link
                        key={link?.href}
                        href={link?.href}
                        className={`block px-4 py-3 font-mono text-xs tracking-wider transition-colors duration-150 border-b border-white/5 last:border-0 ${
                          pathname === link?.href ? 'text-white bg-white/5' : 'text-white/50 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {link?.label}
                      </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Global Search Bar — desktop */}
            <button
              onClick={() => handleSearchOpen()}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 transition-all duration-200 group"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 4,
                minWidth: 180,
              }}
              aria-label="Open global search (press / or ⌘K)"
              title="Search platform — press / or ⌘K"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ color: 'rgba(201,168,76,0.5)', flexShrink: 0 }} aria-hidden="true">
                <circle cx="6.5" cy="6.5" r="4.5" />
                <path d="M10.5 10.5l3 3" />
              </svg>
              <span className="font-mono text-[10px] tracking-wider flex-1 text-left" style={{ color: 'rgba(212,197,169,0.35)', fontStyle: 'italic' }}>
                Search platform…
              </span>
              <div style={{ display: 'flex', gap: 3 }}>
                <span className="font-mono text-[8px] px-1.5 py-0.5" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: 'rgba(212,197,169,0.3)' }}>
                  /
                </span>
                <span className="font-mono text-[8px] px-1.5 py-0.5" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: 'rgba(212,197,169,0.3)' }}>
                  ⌘K
                </span>
              </div>
            </button>

            {/* CTA + Search + Notification Bell */}
            <div className="hidden md:flex items-center gap-3">
              {/* Search icon (compact, for md screens without lg search bar) */}
              <button
                onClick={() => handleSearchOpen()}
                className="lg:hidden flex items-center justify-center w-8 h-8 text-white/40 hover:text-yellow-400 transition-colors duration-200"
                aria-label="Search platform"
                title="Search"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="6.5" cy="6.5" r="4.5" />
                  <path d="M10.5 10.5l3 3" />
                </svg>
              </button>

              {/* Notification Bell */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => { setNotifOpen(v => !v); if (!notifOpen) loadNotifications(); }}
                  className="relative flex items-center justify-center w-8 h-8 transition-colors duration-200"
                  style={{ color: notifOpen ? '#c9a84c' : 'rgba(212,197,169,0.4)' }}
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 flex items-center justify-center font-mono text-[8px] font-black rounded-full"
                      style={{
                        minWidth: 16, height: 16, padding: '0 3px',
                        background: '#D4A82A', color: '#060d12',
                        boxShadow: '0 0 8px rgba(212,168,42,0.6)',
                      }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Panel */}
                {notifOpen && (
                  <div
                    className="absolute top-full right-0 mt-2 w-80 shadow-2xl border border-white/10 overflow-hidden"
                    style={{ background: '#0d1a14', zIndex: 100 }}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/8" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#c9a84c', fontSize: 12 }}>◈</span>
                        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: '#F5F0E8' }}>Notifications</span>
                        {unreadCount > 0 && (
                          <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(212,168,42,0.15)', color: '#D4A82A', border: '1px solid rgba(212,168,42,0.3)' }}>
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          className="font-mono text-[8px] uppercase tracking-widest transition-colors"
                          style={{ color: 'rgba(212,197,169,0.4)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(212,197,169,0.4)'; }}
                        >
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* Notification list */}
                    <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
                      {notifLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>Loading…</span>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="py-10 text-center">
                          <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }}>◎</div>
                          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>No notifications yet</p>
                          <p style={{ color: 'rgba(212,197,169,0.2)', fontSize: 11, marginTop: 4 }}>Engage with the community to receive updates</p>
                        </div>
                      ) : (
                        notifications.map(notif => {
                          const icon = NOTIF_ICONS[notif.type] || '◎';
                          const color = NOTIF_COLORS[notif.type] || '#c9a84c';
                          return (
                            <div
                              key={notif.id}
                              className="flex items-start gap-3 px-4 py-3 border-b cursor-pointer transition-all duration-150"
                              style={{
                                borderColor: 'rgba(255,255,255,0.04)',
                                background: notif.is_read ? 'transparent' : 'rgba(212,168,42,0.04)',
                              }}
                              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = notif.is_read ? 'transparent' : 'rgba(212,168,42,0.04)'; }}
                              onClick={() => {
                                if (!notif.is_read) markOneRead(notif.id);
                                if (notif.link) window.location.href = notif.link;
                              }}
                            >
                              <div
                                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs mt-0.5"
                                style={{ background: `${color}15`, border: `1px solid ${color}30`, color }}
                              >
                                {icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-[10px] font-bold" style={{ color: notif.is_read ? 'rgba(245,240,232,0.6)' : '#F5F0E8', lineHeight: 1.4 }}>
                                  {notif.title}
                                </p>
                                {notif.body && (
                                  <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.4, marginTop: 2 }}>
                                    {notif.body}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  {notif.actor_name && (
                                    <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>
                                      {notif.actor_name}
                                    </span>
                                  )}
                                  <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.25)' }}>
                                    {timeAgo(notif.created_at)}
                                  </span>
                                </div>
                              </div>
                              {!notif.is_read && (
                                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#D4A82A' }} />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2.5 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <Link
                        href="/user-dashboard"
                        onClick={() => setNotifOpen(false)}
                        className="font-mono text-[9px] uppercase tracking-widest transition-colors"
                        style={{ color: 'rgba(201,168,76,0.5)' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.5)'; }}
                      >
                        View My Journey Dashboard →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                href="/keys-to-kulli-haal"
                className="font-mono text-xs tracking-widest uppercase text-black bg-white hover:bg-white/80 transition-colors duration-200 px-5 py-2.5"
              >
                Begin Journey
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex flex-col gap-1.5 p-2"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu — Accordion Drawer */}
        <div
          className={`md:hidden transition-all duration-400 overflow-hidden ${
            mobileOpen ? 'max-h-[90vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0'
          }`}
          style={{ background: 'rgba(28,43,37,0.99)' }}
        >
          <div className="px-4 py-4 flex flex-col gap-0 border-t border-white/10">

            {/* Top utility row */}
            <div className="flex items-center justify-between py-3 border-b border-white/5 mb-2">
              <Link href="/homepage" onClick={() => setMobileOpen(false)} className="font-mono text-xs tracking-widest uppercase text-white/50 hover:text-white transition-colors">
                ◈ Hub
              </Link>
            </div>

            {/* Notification badge */}
            {unreadCount > 0 && (
              <div className="flex items-center gap-2 py-2 border-b border-white/5 mb-1">
                <span style={{ color: '#D4A82A', fontSize: 12 }}>◈</span>
                <span className="font-mono text-xs" style={{ color: '#D4A82A' }}>{unreadCount} new notification{unreadCount > 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Accordion sections */}
            {MOBILE_SECTIONS.map((section) => (
              <div key={section.id} className="border-b border-white/5">
                {/* Section header toggle */}
                <button
                  onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                  className="w-full flex items-center justify-between py-3 px-1 text-left transition-colors"
                  style={{ color: openSection === section.id ? '#c9a84c' : 'rgba(212,197,169,0.55)' }}
                >
                  <span className="font-mono text-[11px] tracking-widest uppercase flex items-center gap-2">
                    <span style={{ color: openSection === section.id ? '#c9a84c' : 'rgba(212,197,169,0.3)' }}>{section.icon}</span>
                    {section.label}
                  </span>
                  <span
                    className="font-mono text-xs transition-transform duration-200"
                    style={{ transform: openSection === section.id ? 'rotate(180deg)' : 'rotate(0deg)', color: 'rgba(212,197,169,0.3)' }}
                  >
                    ▾
                  </span>
                </button>

                {/* Section links */}
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: openSection === section.id ? '500px' : '0px', opacity: openSection === section.id ? 1 : 0 }}
                >
                  <div className="pb-2 pl-4 flex flex-col gap-0">
                    {section.links.map((link) => (
                      <Link
                        key={`${section.id}:${link.label}`}
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="font-mono text-xs tracking-wider py-2.5 border-b border-white/[0.03] transition-colors hover:text-white"
                        style={{ color: link.pinned ? 'rgba(212,168,42,0.85)' : pathname === link.href ? '#c9a84c' : 'rgba(212,197,169,0.4)' }}
                      >
                        {link.pinned ? `✦ ${link.label}` : link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Bottom actions */}
            <div className="flex flex-col gap-2 pt-4">
              <button
                onClick={() => { setMobileOpen(false); handleSearchOpen(); }}
                className="font-mono text-xs tracking-widest uppercase text-white/40 hover:text-white transition-colors py-2 text-left"
              >
                🔍 Search Platform
              </button>
              <Link
                href="/keys-to-kulli-haal"
                onClick={() => setMobileOpen(false)}
                className="font-mono text-xs tracking-widest uppercase text-black bg-white px-5 py-3 text-center mt-1"
              >
                Begin Journey
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}